import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true
    }
  ]
})
export class ImageUploadComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = 'Upload Image';
  @Input() accept: string = 'image/*';
  @Input() maxSize: number = 5 * 1024 * 1024; // 5MB default
  @Input() uploadUrl: string = '/files/upload'; // Default upload endpoint
  @Input() previewWidth: string = '200px';
  @Input() previewHeight: string = 'auto';
  @Input() showRemoveButton: boolean = true;
  @Input() existingImageUrl: string = '';
  @Input() fieldType: string = ''; // For vendor branding: corporate_logo, corporate_main_bar, etc.
  @Input() networkId: number | null = null; // Network ID for vendor uploads

  @Output() imageUploaded = new EventEmitter<string>();
  @Output() imageRemoved = new EventEmitter<void>();
  @Output() uploadError = new EventEmitter<string>();

  imageUrl: string = '';
  isUploading: boolean = false;
  errorMessage: string = '';
  inputId: string = 'image-upload-' + Math.random().toString(36).substr(2, 9);
  private imageTimestamp: number = 0; // Cache timestamp for cache-busting

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // Generate unique ID for the file input based on label
    if (this.label) {
      const sanitizedLabel = this.label.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
      this.inputId = 'image-upload-' + sanitizedLabel + '-' + Math.random().toString(36).substr(2, 9);
    }
  }

  writeValue(value: string): void {
    if (value) {
      this.imageUrl = value;
      // Reset timestamp when setting a new URL
      this.imageTimestamp = 0;
    } else if (this.existingImageUrl) {
      this.imageUrl = this.existingImageUrl;
      this.imageTimestamp = 0;
    } else {
      this.imageUrl = '';
      this.imageTimestamp = 0;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Please select an image file';
      this.uploadError.emit(this.errorMessage);
      return;
    }

    // Validate file size
    if (file.size > this.maxSize) {
      this.errorMessage = `File size exceeds ${this.maxSize / 1024 / 1024}MB limit`;
      this.uploadError.emit(this.errorMessage);
      return;
    }

    this.errorMessage = '';
    this.isUploading = true;

    // Create temporary preview (data URL) for immediate feedback
    const reader = new FileReader();
    reader.onload = (e: any) => {
      // Show temporary preview while uploading
      this.imageUrl = e.target.result;
    };
    reader.readAsDataURL(file);

    // Upload file
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional parameters if provided (for vendor branding uploads)
    if (this.networkId) {
      formData.append('networkId', this.networkId.toString());
    }
    if (this.fieldType) {
      formData.append('fieldType', this.fieldType);
    }

    this.http.post<any>(this.uploadUrl, formData).subscribe(
      (response) => {
        this.isUploading = false;
        // Handle different response formats
        if (response.success && (response.url || response.path)) {
          const uploadedUrl = response.url || response.path;
          // Ensure URL starts with / if it's a relative path
          const finalUrl = uploadedUrl.startsWith('http') ? uploadedUrl : (uploadedUrl.startsWith('/') ? uploadedUrl : '/' + uploadedUrl);
          this.imageUrl = finalUrl;
          // Set timestamp for cache-busting (only once when URL is set)
          this.imageTimestamp = Date.now();
          this.onChange(finalUrl);
          this.onTouched();
          this.imageUploaded.emit(finalUrl);
        } else if (response.url || response.path || response.fileUrl) {
          // Fallback for other response formats
          const uploadedUrl = response.url || response.path || response.fileUrl;
          const finalUrl = uploadedUrl.startsWith('http') ? uploadedUrl : (uploadedUrl.startsWith('/') ? uploadedUrl : '/' + uploadedUrl);
          this.imageUrl = finalUrl;
          // Set timestamp for cache-busting (only once when URL is set)
          this.imageTimestamp = Date.now();
          this.onChange(finalUrl);
          this.onTouched();
          this.imageUploaded.emit(finalUrl);
        } else if (response.error) {
          this.errorMessage = response.error;
          this.uploadError.emit(this.errorMessage);
          // Reset to existing image on error
          this.imageUrl = this.existingImageUrl || '';
        } else {
          this.errorMessage = 'Upload failed: Invalid response';
          this.uploadError.emit(this.errorMessage);
          this.imageUrl = this.existingImageUrl || '';
        }
      },
      (error) => {
        this.isUploading = false;
        this.errorMessage = error.error?.message || 'Upload failed';
        this.uploadError.emit(this.errorMessage);
        console.error('Upload error:', error);
      }
    );
  }

  removeImage(): void {
    this.imageUrl = '';
    this.existingImageUrl = '';
    this.onChange('');
    this.onTouched();
    this.imageRemoved.emit();
    // Reset file input
    const input = document.querySelector(`#${this.inputId}`) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  onImageError(): void {
    // Only show error if it's not a data URL (which means it's a server URL that failed)
    if (!this.imageUrl.startsWith('data:')) {
      this.errorMessage = 'Failed to load image: ' + this.imageUrl;
      console.error('Image load error for URL:', this.imageUrl);
    }
  }

  onImageLoad(): void {
    // Clear any previous error when image loads successfully
    if (this.errorMessage && this.errorMessage.includes('Failed to load image')) {
      this.errorMessage = '';
    }
  }

  getImageSrc(): string {
    if (!this.imageUrl) {
      return '';
    }
    // If it's already a data URL or absolute URL, return as-is
    if (this.imageUrl.startsWith('data:') || this.imageUrl.startsWith('http')) {
      return this.imageUrl;
    }
    // For relative paths, ensure they start with /
    // Since base href is /main/, we need to ensure /files/ paths work correctly
    // Absolute paths starting with / should work, but add cache busting if needed
    let finalUrl = this.imageUrl.startsWith('/') ? this.imageUrl : '/' + this.imageUrl;
    
    // Add cache busting only if timestamp is set (for newly uploaded images)
    // Use a stable timestamp (set once) to avoid ExpressionChangedAfterItHasBeenCheckedError
    if (this.imageTimestamp > 0 && !finalUrl.includes('?')) {
      finalUrl = finalUrl + '?t=' + this.imageTimestamp;
    }
    return finalUrl;
  }
}
